'use client';

import { useState, type ComponentType, type ReactNode } from 'react';
import { Collapse, Drawer, Skeleton } from '@mui/material';
import {
  Check,
  ChevronDown,
  CircleCheck,
  CircleX,
  Clock,
  Copy,
  Hourglass,
  Layers,
  Repeat,
  Send,
  X,
} from 'lucide-react';
import { cn, currencyFormatter } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { ModalCloseButton } from './ModalShell';
import { formatDateTime } from '@/lib/utils/date';
import CircleDollarBuy from '@/components/Icons/CircleDollarBuy';
import CircleDollarSell from '@/components/Icons/CircleDollarSell';
import Empty from '../ui/Empty';
import useTradingHistory, { type TradingActivity } from '../hooks/use-trading-history';
import useWithdrawRecords, {
  useWithdrawConfig,
  WithdrawStatus,
  type WithdrawConfig,
  type WithdrawProgressStep,
  type WithdrawRecord,
} from '../hooks/use-withdraw';

function CopyButton({ value }: { value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label={t('Copy')}
      className="text-[#A772FF] transition-colors hover:text-[#B98AFF]"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // 剪贴板不可用时静默失败
        }
      }}
    >
      {copied ? (
        <Check className="size-3" strokeWidth={1.5} />
      ) : (
        <Copy className="size-3" strokeWidth={1} />
      )}
    </button>
  );
}

/** 币/链小图标：有 URL 用图片，否则首字母圆形占位（与 DepositModal 一致） */
export function AssetBadge({ name, iconUrl }: { name: string; iconUrl?: string }) {
  if (iconUrl) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={iconUrl} alt={name} className="size-4 shrink-0 rounded-full object-cover" />;
  }
  return (
    <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-[#2a2a2a] text-[8px] font-semibold text-[#B7B9BC]">
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

export function DetailRow({
  label,
  value,
  icon,
  copyValue,
}: {
  label: string;
  value: ReactNode;
  /** 值左侧的币/链图标 */
  icon?: ReactNode;
  copyValue?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2.5 text-xs leading-6">
      <span className="shrink-0 text-[#757575]">{label}</span>
      <span className="flex min-w-0 items-center gap-1.5 text-right text-[#B7B9BC]">
        {icon}
        <span className="truncate">{value}</span>
        {copyValue && <CopyButton value={copyValue} />}
      </span>
    </div>
  );
}

/** 展开详情底部的 Telegram 求助条（提现/交易卡片共用） */
function TelegramHelpBar({ telegramUrl }: { telegramUrl: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-[#191919] px-3 py-2">
      <span className="flex items-center gap-2.5 text-xs leading-4.5 text-[#B7B9BC]">
        <span className="bg-[#B7B9BC] rounded-full size-4 inline-flex items-center justify-center text-black">
          ?
        </span>
        {t('Need help? Join our Telegram.')}
      </span>
      <a
        href={telegramUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex h-6.5 shrink-0 items-center gap-2 rounded-md border border-[#212121] px-2 text-sm leading-5 text-[#B7B9BC] transition-colors hover:border-[#3F3F46]"
      >
        <span className="bg-[#B7B9BC] rounded-full size-3.5 inline-flex items-center justify-center">
          <Send className="size-2.5" fill="#000" strokeWidth={1.5} />
        </span>
        {t('Join')}
      </a>
    </div>
  );
}

// ---------------- 提现申请记录（useWithdrawRecords） ----------------

export type WithdrawalStep = 'submitted' | 'under_review' | 'processing' | 'completed';

const STEP_ORDER: WithdrawalStep[] = ['submitted', 'under_review', 'processing', 'completed'];
// 文案复用提现弹窗流程说明的既有 i18n key
const STEP_LABELS: Record<WithdrawalStep, string> = {
  submitted: 'Submitted',
  under_review: 'Under review',
  processing: 'Processing',
  completed: 'Completed',
};

/** 提现申请展示记录 */
export interface TransactionRecord {
  id: string;
  /** 展示金额，如 '10,000.00' */
  amount: string;
  token: string;
  network: string;
  /** 展示地址（已截断），如 '0x12...aBcD' */
  address: string;
  /** 申请时间展示文本，如 'Jun 26, 2025 14:10' */
  requestedAt: string;
  status: 'in_progress' | 'completed' | 'failed';
  /** in_progress 时的当前步骤（此步骤前均视为已完成） */
  currentStep?: WithdrawalStep;
  /** 各步骤的副标签（时间/时长），如 { submitted: 'Jun 26 15:30', under_review: '1-48h' } */
  stepNotes?: Partial<Record<WithdrawalStep, string>>;
  /** failed 时的原因 */
  failReason?: string;
  /** 展开详情（缺省的行不展示） */
  detail?: {
    receiveToken?: string;
    /** 币图标 URL；缺省显示首字母圆形占位 */
    receiveTokenIconUrl?: string;
    receiveChain?: string;
    /** 链图标 URL；缺省显示首字母圆形占位 */
    receiveChainIconUrl?: string;
    recipientAddress?: string;
    requestTime?: string;
    requestId?: string;
  };
}

function StatusIcon({ status }: { status: TransactionRecord['status'] }) {
  if (status === 'in_progress') {
    return (
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#FFBE49]">
        <Hourglass className="size-4 text-[#292D32]" strokeWidth={1.5} />
      </span>
    );
  }
  if (status === 'completed') {
    return (
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#14C784]">
        <Check className="size-3.5 text-black" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#FF775F]">
      <X className="size-3.5 text-black" strokeWidth={2.5} />
    </span>
  );
}

function StatusTag({ status }: { status: 'completed' | 'failed' }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'flex h-4.5 shrink-0 items-center rounded-lg px-2 text-xs leading-5',
        status === 'completed'
          ? 'bg-[#14C784]/10 text-[#14C784]'
          : 'bg-[#FF775F]/10 text-[#FF775F]',
      )}
    >
      {status === 'completed' ? t('Completed') : t('Failed')}
    </span>
  );
}

/**
 * 提现进度条（Figma node 1170-20836）：
 * 贯通细线垂直居中于 24px 图标行（左右各缩进半个图标宽），紫色段覆盖起点到当前节点；
 * 节点圆心沿线均匀分布（首节点在线起点、末节点在线终点）；
 * 完成=紫圆对勾，当前=紫圆时钟，未到=灰圆环点。
 */
function ProgressStepper({
  currentStep = 'under_review',
  stepNotes,
}: {
  currentStep?: WithdrawalStep;
  stepNotes?: Partial<Record<WithdrawalStep, string>>;
}) {
  const { t } = useTranslation();
  const currentIndex = STEP_ORDER.indexOf(currentStep);
  // completed = 流程已完结，所有节点（含最后一个）都渲染为完成态
  const finished = currentStep === 'completed';
  const progress = Math.max(currentIndex, 0) / (STEP_ORDER.length - 1);

  return (
    <div className="relative">
      {/* 贯通线：首节点中心 → 末节点中心 */}
      <div className="absolute left-3 right-3 top-3 h-px bg-[#757575]" />
      <div
        className="absolute left-3 top-3 h-px bg-[#A772FF] transition-[width] duration-300"
        style={{ width: `calc((100% - 24px) * ${progress})` }}
      />
      {/* 节点绝对定位：首节点贴线起点、末节点贴线终点、中间圆心沿线均匀分布 */}
      <div className="relative h-16.5">
        {STEP_ORDER.map((step, index) => {
          const done = index < currentIndex || finished;
          const active = !finished && index === currentIndex;
          const isFirst = index === 0;
          const isLast = index === STEP_ORDER.length - 1;
          return (
            <div
              key={step}
              className={cn(
                'absolute top-0 flex flex-col gap-2.5',
                isFirst
                  ? 'left-0 items-start'
                  : isLast
                    ? 'right-0 items-end'
                    : 'items-center -translate-x-1/2',
              )}
              style={
                !isFirst && !isLast
                  ? // 圆心 = 线起点(12px) + 线长 × 进度占比
                    { left: `calc(12px + (100% - 24px) * ${index / (STEP_ORDER.length - 1)})` }
                  : undefined
              }
            >
              {done ? (
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#A772FF]">
                  <Check className="size-3.5 text-black" strokeWidth={2.5} />
                </span>
              ) : active ? (
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#A772FF]">
                  <Clock className="size-3.5 text-black" strokeWidth={2} />
                </span>
              ) : (
                // 卡片底色遮住穿过的贯通线
                <span className="flex size-6 shrink-0 items-center justify-center bg-[#0D0D0D]">
                  <span className="flex size-3.5 items-center justify-center rounded-full border-[1.5px] border-[#7B7B7B]">
                    <span className="size-1.5 rounded-full bg-[#757575]" />
                  </span>
                </span>
              )}
              <div
                className={cn(
                  'flex flex-col text-[11px] leading-4 text-[#B7B9BC]',
                  isFirst
                    ? 'items-start text-left'
                    : isLast
                      ? 'items-end text-right'
                      : 'items-center text-center',
                )}
              >
                <span>{t(STEP_LABELS[step])}</span>
                {stepNotes?.[step] && <span>{stepNotes[step]}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** 提现目标链 id → 展示名/图标（Polymarket bridge 支持的链） */
const CHAIN_META: Record<number, { name: string; domain: string }> = {
  1: { name: 'Ethereum', domain: 'ethereum.org' },
  10: { name: 'Optimism', domain: 'optimism.io' },
  56: { name: 'BNB Smart Chain', domain: 'bnbchain.org' },
  137: { name: 'Polygon', domain: 'polygon.technology' },
  143: { name: 'Monad', domain: 'monad.xyz' },
  999: { name: 'HyperEVM', domain: 'hyperliquid.xyz' },
  4663: { name: 'Robinhood', domain: 'robinhood.com' },
  8453: { name: 'Base', domain: 'base.org' },
  42161: { name: 'Arbitrum', domain: 'arbitrum.io' },
  57073: { name: 'Ink', domain: 'inkonchain.com' },
  728126428: { name: 'Tron', domain: 'trondao.org' },
  8253038: { name: 'Bitcoin', domain: 'bitcoin.org' },
  1151111081099710: { name: 'Solana', domain: 'solana.com' },
};

function favicon(domain: string) {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=256`;
}

function shortenAddress(value: string, beforeLen = 7, afterLen = 5) {
  return value.length > 12 ? `${value.slice(0, beforeLen)}...${value.slice(-afterLen)}` : value;
}

/** 进度条节点副标签用的短时间，如 'Jun 26 15:30' */
function formatShortTime(date: Date) {
  const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart} ${timePart}`;
}

/** 申请编号展示格式，如 'WD-20250626-1530'（与提现成功视图一致） */
function buildRequestId(date: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `WD-${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}`;
}

/** 提现申请记录（API）→ 展示记录 */
function toTransactionRecord(
  record: WithdrawRecord,
  t: (key: string) => string,
  config: WithdrawConfig | null,
): TransactionRecord {
  const chain = CHAIN_META[record.chainId];
  const network = chain?.name ?? `Chain ${record.chainId}`;
  const created = new Date(record.createdAt);
  const requestedAt = formatDateTime(created, 'MMM DD, YYYY HH:mm');
  // amount 为 decimal 字符串；-1 表示全部提现
  const amountValue = Number(record.amount);
  const amount =
    amountValue < 0
      ? t('All')
      : amountValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  const inProgress =
    record.status === WithdrawStatus.Recorded || record.status === WithdrawStatus.Processing;
  const status: TransactionRecord['status'] = inProgress
    ? 'in_progress'
    : record.status === WithdrawStatus.Success
      ? 'completed'
      : 'failed';
  const reason = record.reject_reason || record.message;
  const failReason =
    status === 'failed'
      ? (reason ? t(reason) : '') ||
        (record.status === WithdrawStatus.Cancelled ? t('Cancelled') : t('Rejected'))
      : undefined;
  const stepMap: Record<WithdrawProgressStep, WithdrawalStep> = {
    submitted: 'submitted',
    reviewing: 'under_review',
    signing: 'processing',
    sending: 'processing',
    done: 'completed',
    rejected: 'completed',
    canceled: 'completed',
  };
  const currentStep = record.step
    ? stepMap[record.step]
    : record.status === WithdrawStatus.Recorded
      ? 'under_review'
      : 'processing';
  const estimatedHours =
    record.tier === 'auto' ? (config?.auto_hours ?? 6) : (config?.manual_hours ?? 24);
  const signatureProgress =
    record.step === 'signing' && record.signatures_required != null
      ? `${record.signatures_done ?? 0}/${record.signatures_required} signatures`
      : null;

  return {
    id: String(record.id),
    amount,
    token: 'USDC',
    network,
    address: shortenAddress(record.address),
    requestedAt,
    status,
    ...(inProgress && {
      currentStep,
      stepNotes: {
        submitted: formatShortTime(created),
        under_review: `≤${estimatedHours}h`,
        ...(record.status === WithdrawStatus.Processing && {
          processing: signatureProgress ?? `≤${estimatedHours}h`,
        }),
      },
    }),
    // 已完结（成功/失败/取消/拒绝）：进度条所有节点展示为完成
    ...(!inProgress && {
      currentStep: 'completed' as const,
      stepNotes: { submitted: formatShortTime(created) },
    }),
    failReason,
    detail: {
      receiveToken: 'USDC',
      receiveTokenIconUrl: favicon('usdc.com'),
      receiveChain: network,
      receiveChainIconUrl: chain ? favicon(chain.domain) : undefined,
      recipientAddress: record.address,
      requestTime: requestedAt,
      requestId: record.uid || buildRequestId(created),
    },
  };
}

/** 提现申请记录卡片（含进度条与展开详情） */
function RecordCard({ record, telegramUrl }: { record: TransactionRecord; telegramUrl?: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const detail = record.detail;
  const expandable = record.status === 'in_progress' || !!detail;

  return (
    <article className="rounded-xl border border-[#1F201F] bg-[#0D0D0D] p-3">
      <button
        type="button"
        className="flex w-full items-start gap-3 text-left"
        onClick={() => expandable && setExpanded((v) => !v)}
      >
        <StatusIcon status={record.status} />
        <div className="flex w-full min-w-0 flex-col items-start gap-2">
          <div className="flex min-w-0 gap-2">
            <span className="text-base leading-4.5 text-white">
              {record.amount} {record.token}
            </span>
            {record.status === 'failed' && <StatusTag status={record.status} />}
          </div>
          <div className="flex flex-col gap-1 text-xs leading-4 text-[#757575]">
            <span>
              {record.network} · {record.address}
            </span>
            <span>
              {t('Requested')}: {record.requestedAt}
            </span>
            {record.status === 'failed' && record.failReason && (
              <span className="text-[#FF775F]">
                {t('Reason')}: {record.failReason}
              </span>
            )}
          </div>
        </div>
        {expandable && (
          <ChevronDown
            className={cn(
              'mt-0.5 size-4.5 shrink-0 text-[#B7B9BC] transition-transform',
              expanded && 'rotate-180',
            )}
            strokeWidth={1.5}
          />
        )}
      </button>

      {/* 提现申请记录（含失败/取消/拒绝）：进度条常显 */}
      {record.currentStep && (
        <div className="mt-3">
          <ProgressStepper currentStep={record.currentStep} stepNotes={record.stepNotes} />
        </div>
      )}

      {/* 展开详情：Collapse 平滑过渡高度 */}
      {detail && (
        <Collapse in={expanded} timeout={300} unmountOnExit>
          <div className="flex flex-col gap-5 pt-3">
            <div className="border-t border-[#272727] pt-3">
              <DetailRow label={t('Amount')} value={`${record.amount} ${record.token}`} />
              {detail.receiveToken && (
                <DetailRow
                  label={t('Receive token')}
                  value={detail.receiveToken}
                  icon={
                    <AssetBadge name={detail.receiveToken} iconUrl={detail.receiveTokenIconUrl} />
                  }
                />
              )}
              {detail.receiveChain && (
                <DetailRow
                  label={t('Receive chain')}
                  value={detail.receiveChain}
                  icon={
                    <AssetBadge name={detail.receiveChain} iconUrl={detail.receiveChainIconUrl} />
                  }
                />
              )}
              <DetailRow
                label={t('Recipient address')}
                value={shortenAddress(detail.recipientAddress ?? record.address)}
                copyValue={detail.recipientAddress ?? record.address}
              />
              {detail.requestTime && (
                <DetailRow label={t('Request time')} value={detail.requestTime} />
              )}
              {detail.requestId && (
                <DetailRow
                  label={t('Request ID')}
                  value={detail.requestId}
                  copyValue={detail.requestId}
                />
              )}
            </div>

            {telegramUrl && <TelegramHelpBar telegramUrl={telegramUrl} />}
          </div>
        </Collapse>
      )}
    </article>
  );
}

// ---------------- 交易历史（useTradingHistory） ----------------

interface ActivityTypeMeta {
  /** 卡片标题动作词（后接 outcome，如 'Buy Yes'） */
  label: string;
  /** 图标颜色（Figma vuesax 图标描边色） */
  color: string;
  /** 36x36 图标底色（Figma 20% 透明度色块） */
  iconBg: string;
  Icon: ComponentType<{ size?: number | string; color?: string }>;
}

/** activity.type → 图标/配色（Figma node 18-53719；split 设计稿未覆盖，沿用 merge 样式） */
const ACTIVITY_TYPE_META: Record<string, ActivityTypeMeta> = {
  buy: { label: 'Buy', color: '#73B4FF', iconBg: 'rgba(57,97,251,0.2)', Icon: CircleDollarBuy },
  sell: { label: 'Sell', color: '#8CFF19', iconBg: 'rgba(76,140,13,0.2)', Icon: CircleDollarSell },
  redeem: { label: 'Redeem', color: '#8CFF19', iconBg: 'rgba(76,140,13,0.2)', Icon: CircleCheck },
  lost: { label: 'Lost', color: '#FF6B16', iconBg: 'rgba(255,107,22,0.2)', Icon: CircleX },
  merge: { label: 'Merge', color: '#8CFF19', iconBg: 'rgba(140,255,25,0.2)', Icon: Layers },
  split: { label: 'Split', color: '#8CFF19', iconBg: 'rgba(140,255,25,0.2)', Icon: Layers },
  convert: { label: 'Convert', color: '#A772FF', iconBg: 'rgba(167,114,255,0.2)', Icon: Repeat },
};

const DEFAULT_TYPE_META: ActivityTypeMeta = {
  label: '',
  color: '#8CFF19',
  iconBg: 'rgba(140,255,25,0.2)',
  Icon: Layers,
};

const formatAmount = (value: number) =>
  currencyFormatter(Math.abs(value), { notation: 'standard' });

/** 交易活动卡片。Figma: Pythra 产品版本管理 node 18-53719 */
function ActivityCard({
  activity,
  telegramUrl,
}: {
  activity: TradingActivity;
  telegramUrl?: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const meta = ACTIVITY_TYPE_META[activity.type] ?? {
    ...DEFAULT_TYPE_META,
    label: activity.type,
  };
  const marketName = activity.group_title;

  const payout = activity.total_payout;
  const amountText =
    payout > 0 ? `+${formatAmount(payout)}` : payout < 0 ? `-${formatAmount(payout)}` : '0';
  const amountColor = payout > 0 ? 'text-[#14C784]' : payout < 0 ? 'text-[#FF2D2D]' : 'text-white';

  // 展开详情行：仅展示有值的字段
  const detailRows = [
    activity.price != null && { label: t('Price'), value: `${(activity.price * 100).toFixed(1)}¢` },
    activity.shares != null && {
      label: t('Shares'),
      value: activity.shares.toLocaleString('en-US'),
    },
    activity.trade_value != null && {
      label: t('Trade value'),
      value: formatAmount(activity.trade_value),
    },
    activity.fee != null && { label: t('Fee'), value: formatAmount(activity.fee) },
    activity.rebate != null && { label: t('Rebate'), value: formatAmount(activity.rebate) },
    activity.payout != null && { label: t('Payout'), value: formatAmount(activity.payout) },
    activity.usdc_received != null && {
      label: t('USDC received'),
      value: formatAmount(activity.usdc_received),
    },
  ].filter((row): row is { label: string; value: string } => Boolean(row));
  const expandable = detailRows.length > 0;

  return (
    <article className="rounded-xl border border-[#18191A] bg-[#101111] p-3">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2.5 text-left"
        onClick={() => expandable && setExpanded((v) => !v)}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="flex size-9 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: meta.iconBg }}
          >
            <meta.Icon size={20} color={meta.color} />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 items-center gap-1 text-xs leading-4.5">
              <span className="shrink-0 font-medium text-white capitalize">{t(meta.label)}</span>
              <span
                className={cn(
                  'shrink-0 font-medium text-[#14C784] capitalize',
                  activity.outcome?.toLowerCase() === 'no' && 'text-[#FF6B6B]',
                )}
              >
                {activity.outcome}
              </span>
              {marketName && <span className="truncate text-white">{marketName}</span>}
            </div>
            <span className="text-xs leading-3.5 text-[#757575]">
              {activity.timestamp ? formatDateTime(activity.timestamp, 'MMM D, HH:mm') : '--'}
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          <div className="flex flex-col items-end gap-1">
            <span className={cn('text-sm font-medium leading-4.5', amountColor)}>{amountText}</span>
            {activity.shares != null && (
              <span className="text-xs leading-4.5 text-[#757575]">
                {activity.shares.toLocaleString('en-US')} {t('share')}
              </span>
            )}
          </div>
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-[#757575] transition-transform',
              expanded && 'rotate-180',
              !expandable && 'invisible',
            )}
            strokeWidth={1.5}
          />
        </div>
      </button>

      {/* 展开详情：Collapse 平滑过渡高度 */}
      {expandable && (
        <Collapse in={expanded} timeout={300} unmountOnExit>
          <div className="flex flex-col gap-5 pt-3">
            <div className="border-t border-[#272727] pt-3">
              {detailRows.map((row) => (
                <DetailRow key={row.label} label={row.label} value={row.value} />
              ))}
            </div>

            {/* {telegramUrl && <TelegramHelpBar telegramUrl={telegramUrl} />} */}
          </div>
        </Collapse>
      )}
    </article>
  );
}

export interface TransactionHistoryPanelProps {
  /** Telegram 求助链接；缺省时展开详情不显示求助条 */
  telegramUrl?: string;
}

/**
 * 交易历史面板：In Progress 区为提现申请记录（useWithdrawRecords，已记录/处理中），
 * History 区为 Polymarket 钱包活动（useTradingHistory 分页加载）。
 */
function TransactionHistoryPanel({ telegramUrl }: TransactionHistoryPanelProps) {
  const { t } = useTranslation();
  const { config: withdrawConfig } = useWithdrawConfig();

  // 提现申请（进行中）
  const { records: withdrawRecords, isLoading: isWithdrawLoading } = useWithdrawRecords({
    limit: 50,
    status: [WithdrawStatus.Recorded, WithdrawStatus.Processing],
  });
  const inProgressList = withdrawRecords
    .map((record) => toTransactionRecord(record, t, withdrawConfig))
    .filter((r) => r.status === 'in_progress');

  // History 区：hook 内部 Promise.all 并发拉取交易活动 + 失败类提现（3/4/5），
  // 已按时间降序混合，source 字段区分数据来源
  const { records, isLoading, isValidating, hasMore, setSentinelRef } = useTradingHistory({
    includeFunding: true,
  });

  if ((isLoading || isWithdrawLoading) && inProgressList.length === 0 && records.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={64}
            sx={{ bgcolor: 'grey.900', borderRadius: '12px' }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 text-white">
      {inProgressList.length > 0 && (
        <section className="flex flex-col gap-3">
          <h3 className="text-sm leading-4.5 text-white">{t('In Progress')}</h3>
          <div className="flex flex-col gap-2">
            {inProgressList.map((record) => (
              <RecordCard key={record.id} record={record} telegramUrl={telegramUrl} />
            ))}
          </div>
        </section>
      )}

      <section className="flex flex-col gap-3">
        <h3 className="text-sm leading-4.5 text-white">{t('Completed')}</h3>
        {records.length === 0 ? (
          <Empty description="There is no data to show you right now" />
        ) : (
          <div className="flex flex-col gap-3">
            {records.map((item) =>
              item.source === 'withdraw' ? (
                <RecordCard
                  key={`withdraw-${item.record.uid || item.record.id}`}
                  record={toTransactionRecord(item.record, t, withdrawConfig)}
                  telegramUrl={telegramUrl}
                />
              ) : (
                <ActivityCard
                  key={item.record.id}
                  activity={item.record}
                  telegramUrl={telegramUrl}
                />
              ),
            )}
            {/* 加载下一页中 */}
            {hasMore && isValidating && (
              <Skeleton
                variant="rounded"
                height={64}
                sx={{ bgcolor: 'grey.900', borderRadius: '12px' }}
              />
            )}
            {/* sentinel：进入视口自动加载下一页 */}
            {hasMore && !isValidating && <div ref={setSentinelRef} className="h-px" />}
          </div>
        )}
      </section>
    </div>
  );
}

export interface HistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  /** 透传给交易历史面板的数据（Telegram 求助链接） */
  historyProps?: TransactionHistoryPanelProps;
}

/** 交易历史右侧抽屉，宽度 400 */
export default function HistoryDrawer({ open, onClose, historyProps }: HistoryDrawerProps) {
  const { t } = useTranslation();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        hideBackdrop: true,
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disableRestoreFocus: true,
        disableScrollLock: true,
      }}
      slotProps={{
        root: {
          sx: {
            pointerEvents: 'none',
          },
        },
        paper: {
          sx: {
            width: 400,
            maxWidth: '100vw',
            bgcolor: '#000',
            color: '#fff',
            pointerEvents: 'auto',
          },
        },
      }}
    >
      <div className="flex h-full flex-col border-l border-white/15">
        <header className="sticky top-0 flex h-15 shrink-0 items-center justify-between border-b border-black/10 px-4">
          <h2 className="text-base font-medium text-white/80">{t('History')}</h2>
          <ModalCloseButton onClose={onClose} />
        </header>
        <div className="flex-1 overflow-y-auto p-5">
          <TransactionHistoryPanel {...historyProps} />
        </div>
      </div>
    </Drawer>
  );
}
